// /client/src/components/EditContact.js
// UPDATED: Removed manual opt-in/opt-out overrides. 
// Consent status is now controlled solely by the participant via SMS keywords (JOIN/START/STOP).
// Admins can send opt-in requests but cannot manually change consent status.
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from './Navigation';

// Default confirmation messages
const DEFAULT_OPT_IN_CONFIRMATION = (companyName = 'Next Move') => `You're now subscribed to receiving messages from ${companyName}. Message and data rates may apply. Reply STOP to opt out anytime.`;
const DEFAULT_OPT_OUT_CONFIRMATION = (companyName = 'Next Move') => `You have successfully unsubscribed and will no longer receive messages from ${companyName}. To rejoin at any time, reply START. Message and data rates may apply.`;
// Major US carriers list
const CARRIER_OPTIONS = [
  { value: '', label: 'Select a carrier (optional)' },
  { value: 'att', label: 'AT&T' },
  { value: 'verizon', label: 'Verizon' },
  { value: 'tmobile', label: 'T-Mobile' },
  { value: 'sprint', label: 'Sprint' },
  { value: 'us-cellular', label: 'US Cellular' },
  { value: 'cricket', label: 'Cricket Wireless' },
  { value: 'metro', label: 'Metro by T-Mobile' },
  { value: 'boost', label: 'Boost Mobile' },
  { value: 'visible', label: 'Visible' },
  { value: 'mint', label: 'Mint Mobile' },
  { value: 'google-fi', label: 'Google Fi' },
  { value: 'xfinity', label: 'Xfinity Mobile' },
  { value: 'spectrum', label: 'Spectrum Mobile' },
  { value: 'consumer-cellular', label: 'Consumer Cellular' },
  { value: 'straight-talk', label: 'Straight Talk' },
  { value: 'tracfone', label: 'TracFone' },
  { value: 'simple-mobile', label: 'Simple Mobile' },
  { value: 'other', label: 'Other' }
];

const EditContact = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('contact');
  
  // Contact form state
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    carrier: '',
    role: 'participant',
    status: 'active',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States'
    },
    bio: '',
    emailVerified: false,
    smsVerified: false
  });
  
  // Consent state
  const [consentData, setConsentData] = useState(null);
  const [consentLoading, setConsentLoading] = useState(false);
  const [consentHistory, setConsentHistory] = useState([]);
  const [companyName, setCompanyName] = useState('Next Move');
  
  // Opt-in message state
  const [showOptinForm, setShowOptinForm] = useState(false);
  const [optinMessage, setOptinMessage] = useState('');
  const [customOptInConfirmation, setCustomOptInConfirmation] = useState('');
  const [customOptOutConfirmation, setCustomOptOutConfirmation] = useState('');
  const [showConfirmationMessages, setShowConfirmationMessages] = useState(false);
  
  // UI state
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingContact, setLoadingContact] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');

  // Helper to strip +1 prefix for display
  const stripPhonePrefix = (phone) => {
    if (!phone) return '';
    if (phone.startsWith('+1')) return phone.slice(2);
    if (phone.startsWith('+')) return phone.slice(1);
    return phone;
  };

  // Format phone for API calls
  const formatPhone = (phone) => {
    if (!phone) return '';
    let formatted = phone.trim().replace(/\D/g, '');
    if (formatted.length === 10) formatted = '1' + formatted;
    if (!formatted.startsWith('+')) formatted = '+' + formatted;
    return formatted;
  };

  // Load contact data on mount
  useEffect(() => {
    fetchContact();
  }, [id]);

  // Load consent data when contact is loaded or tab changes
  useEffect(() => {
    if (activeTab === 'consent' && formData.phone && formData.role === 'participant') {
      fetchConsentData();
    }
  }, [activeTab, formData.phone, formData.role]);

  // Fetch company name
  useEffect(() => {
    const fetchCompanyName = async () => {
      try {
        const token = localStorage.getItem('token');
        
        const response = await fetch('/api/company', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          
          if (data.company?.name) {
            setCompanyName(data.company.name);
            
          }
        } else {
          
        }
      } catch (error) {
        console.error('EditContact: Failed to fetch company name:', error);
      }
    };
    fetchCompanyName();
  }, []);

  // Update confirmation defaults when companyName loads
  useEffect(() => {
    if (companyName && !customOptInConfirmation) {
      setCustomOptInConfirmation(DEFAULT_OPT_IN_CONFIRMATION(companyName));
      setCustomOptOutConfirmation(DEFAULT_OPT_OUT_CONFIRMATION(companyName));
    }
  }, [companyName]);

  const fetchContact = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/contacts/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const contact = data.contact;
        
        setFormData({
          email: contact.email || '',
          firstName: contact.firstName || '',
          lastName: contact.lastName || '',
          phone: stripPhonePrefix(contact.phone || ''),
          carrier: contact.carrier || '',
          role: contact.role || 'participant',
          status: contact.status || 'active',
          address: contact.address || {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: 'United States'
          },
          bio: contact.bio || '',
          emailVerified: contact.emailVerified || false,
          smsVerified: contact.smsVerified || false
        });

        // Generate default opt-in message
        const firstName = contact.firstName || '';
        setOptinMessage(
          `Hello${firstName ? ` ${firstName}` : ''}. This is an automated message from the team at ${companyName}. To receive daily prompts and messages from our program, reply JOIN. We recommend adding ${companyName} to your contacts before typing JOIN.\n\nYou can opt out anytime by replying STOP. Message and data rates may apply. Learn more about our privacy practices at nextmovenetwork.com.`
        );
      } else {
        setErrors({ submit: 'Failed to load contact' });
      }
    } catch (error) {
      console.error('Error fetching contact:', error);
      setErrors({ submit: 'Network error loading contact' });
    } finally {
      setLoadingContact(false);
    }
  };

  const fetchConsentData = async () => {
    if (!formData.phone) return;
    
    setConsentLoading(true);
    try {
      const token = localStorage.getItem('token');
      const phoneNumber = formatPhone(formData.phone);
      
      // Get consent status
      const response = await fetch(`/api/twilio/consent/${encodeURIComponent(phoneNumber)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.exists) {
          const prevStatus = consentData?.status;
          setConsentData(data.consent);
          
          // Load custom confirmation messages from consent, or use defaults
          setCustomOptInConfirmation(data.consent.customOptInConfirmation || DEFAULT_OPT_IN_CONFIRMATION(companyName));
          setCustomOptOutConfirmation(data.consent.customOptOutConfirmation || DEFAULT_OPT_OUT_CONFIRMATION(companyName));
          
          // If status changed to opted-in, update local form state and show success
          if (data.consent.status === 'opted-in' && prevStatus !== 'opted-in') {
            setFormData(prev => ({ ...prev, smsVerified: true }));
            if (prevStatus === 'pending') {
              setSuccessMessage('Contact has opted in successfully!');
            }
          } else if (data.consent.status === 'opted-out' && prevStatus !== 'opted-out') {
            setFormData(prev => ({ ...prev, smsVerified: false }));
          }
          
          // Fetch history if consent exists
          const historyResponse = await fetch(
            `/api/twilio/consent/${encodeURIComponent(phoneNumber)}/history`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );
          
          if (historyResponse.ok) {
            const historyData = await historyResponse.json();
            setConsentHistory(historyData.history || []);
          }
        } else {
          setConsentData(null);
          setConsentHistory([]);
          setCustomOptInConfirmation(DEFAULT_OPT_IN_CONFIRMATION(companyName));
          setCustomOptOutConfirmation(DEFAULT_OPT_OUT_CONFIRMATION(companyName));
        }
      }
    } catch (error) {
      console.error('Error fetching consent data:', error);
    } finally {
      setConsentLoading(false);
    }
  };

  // Manual refresh handler for consent data
  const handleRefreshConsent = () => {
    if (formData.phone && formData.role === 'participant') {
      fetchConsentData();
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({
        ...prev,
        phone: digitsOnly
      }));
      // Reset consent data when phone changes
      setConsentData(null);
      setConsentHistory([]);
    } else {
      setFormData(prev => {
        const newData = {
          ...prev,
          [name]: type === 'checkbox' ? checked : value
        };
        
        if (name === 'role') {
          if (value === 'participant') {
            newData.emailVerified = false;
          } else {
            newData.smsVerified = false;
          }
        }
        
        return newData;
      });
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (formData.role === 'admin' || formData.role === 'host') {
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required for Admin and Host roles';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    } else if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (formData.role === 'participant') {
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required for Participants';
      } else if (formData.phone.length !== 10) {
        newErrors.phone = 'Phone number must be exactly 10 digits';
      }
    } else if (formData.phone && formData.phone.length !== 10 && formData.phone.length > 0) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setSuccessMessage('');
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setErrors({ submit: 'Authentication token not found. Please log in again.' });
        return;
      }
      
      const formattedPhone = formData.phone ? '+1' + formData.phone : '';
      
      const updateData = {
        email: formData.email.trim(),
        profile: {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          phone: formattedPhone,
          carrier: formData.carrier,
          address: formData.address,
          bio: formData.bio.trim()
        },
        role: formData.role,
        status: formData.status,
        emailVerified: formData.role === 'participant' ? false : formData.emailVerified,
        smsVerified: (formData.role === 'admin' || formData.role === 'host') ? false : formData.smsVerified
      };
      
      const response = await fetch(`/api/contacts/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });
      
      const responseData = await response.json();
      
      if (response.ok) {
        setSuccessMessage('Contact updated successfully!');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setErrors({ 
          submit: responseData.error || 'Failed to update contact. Please try again.' 
        });
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      setErrors({ 
        submit: 'Network error. Please check your connection and try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendOptIn = async () => {
    if (!formData.phone) {
      setErrors({ consent: 'Phone number is required to send opt-in message' });
      return;
    }

    setLoading(true);
    setErrors({});
    
    try {
      const token = localStorage.getItem('token');
      const phoneNumber = formatPhone(formData.phone);
      
      const response = await fetch('/api/twilio/send-optin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          phoneNumber,
          firstName: formData.firstName,
          lastName: formData.lastName,
          contactId: id,
          customMessage: optinMessage || null,
          customOptInConfirmation: customOptInConfirmation || null,
          customOptOutConfirmation: customOptOutConfirmation || null
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage('Opt-in message sent successfully!');
        setShowOptinForm(false);
        // Refresh consent data
        fetchConsentData();
      } else {
        setErrors({ consent: data.error || 'Failed to send opt-in message' });
      }
    } catch (error) {
      console.error('Error sending opt-in:', error);
      setErrors({ consent: 'Network error sending opt-in message' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfirmationMessages = async () => {
    if (!formData.phone) return;
    
    setLoading(true);
    setErrors({});
    
    try {
      const token = localStorage.getItem('token');
      const phoneNumber = formatPhone(formData.phone);
      
      const response = await fetch(`/api/twilio/consent/${encodeURIComponent(phoneNumber)}/confirmations`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customOptInConfirmation: customOptInConfirmation || null,
          customOptOutConfirmation: customOptOutConfirmation || null
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage('Confirmation messages updated!');
        fetchConsentData();
      } else {
        setErrors({ consent: data.error || 'Failed to update confirmation messages' });
      }
    } catch (error) {
      console.error('Error saving confirmation messages:', error);
      setErrors({ consent: 'Network error saving confirmation messages' });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOptIn = async () => {
    if (!consentData?.id) return;
    
    setLoading(true);
    setErrors({});
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/consent/${consentData.id}/resend-optin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage('Opt-in request resent successfully!');
        fetchConsentData();
      } else {
        setErrors({ consent: data.error || 'Failed to resend opt-in request' });
      }
    } catch (error) {
      console.error('Error resending opt-in:', error);
      setErrors({ consent: 'Network error resending opt-in' });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      'opted-in': 'is-success',
      'opted-out': 'is-danger',
      'pending': 'is-warning',
      'expired': 'is-dark',
      'unknown': 'is-light'
    };
    return badges[status] || 'is-light';
  };

  const emailRequired = formData.role === 'admin' || formData.role === 'host';
  const phoneRequired = formData.role === 'participant';
  const showConsentTab = formData.role === 'participant';

  if (loadingContact) {
    return (
      <section className="theme-light m-0 p-0" data-theme="light">
        <Navigation />
        <div className="container mt-6 pt-6">
          <div className="box has-text-centered">
            <p>Loading contact...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          
          <Navigation />
          
          <div className='mt-2 pt-6 container px-6'>
            <button 
              className="button is-small is-text mb-4"
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>
            
            <div className='is-flex is-flex-direction-rows is-justify-content-space-between is-align-items-center'>
              <h1 className='h1 my-2'>Edit Contact</h1>
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="notification is-success">
                <button 
                  className="delete" 
                  onClick={() => setSuccessMessage('')}
                ></button>
                {successMessage}
              </div>
            )}

            {/* Error Message */}
            {errors.submit && (
              <div className="notification is-danger">
                <button 
                  className="delete" 
                  onClick={() => setErrors(prev => ({ ...prev, submit: '' }))}
                ></button>
                {errors.submit}
              </div>
            )}

            {/* Opted Out Warning Banner - Shows on ALL tabs when contact is opted out */}
            {formData.role === 'participant' && consentData?.status === 'opted-out' && (
              <div className="notification is-danger is-light mb-4">
                <div className="is-flex is-align-items-center">
                  <span className="icon is-large mr-3">
                    <i className="fas fa-ban fa-2x"></i>
                  </span>
                  <div>
                    <p className="has-text-weight-bold is-size-5 mb-1">
                      ⚠️ This contact has opted out of SMS messaging
                    </p>
                    <p className="is-size-6 mb-2">
                      They will not receive any messages until they text <strong>START</strong> to your Twilio number to opt back in.
                    </p>
                    {consentData.lastOptOutDetails && (
                      <p className="is-size-7">
                        Opted out on {formatDate(consentData.lastOptOutDetails.timestamp)} via {consentData.lastOptOutDetails.method?.replace(/-/g, ' ')}
                        {consentData.lastOptOutDetails.podsRemoved > 0 && (
                          <span> • Removed from {consentData.lastOptOutDetails.podsRemoved} pod(s)</span>
                        )}
                        {consentData.lastOptOutDetails.messagesCancelled > 0 && (
                          <span> • {consentData.lastOptOutDetails.messagesCancelled} message(s) cancelled</span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="my-0 py-0 tabs is-boxed">
              <ul className='my-0 py-0'>
                <li className={activeTab === 'contact' ? 'mb-0 pb-0 is-active' : 'mb-0 pb-0'}>
                  <a onClick={() => setActiveTab('contact')}>
                    Contact Info
                  </a>
                </li>
                {showConsentTab && (
                  <li className={activeTab === 'consent' ? 'mb-0 pb-0 is-active' : 'mb-0 pb-0'}>
                    <a onClick={() => setActiveTab('consent')}>
                      SMS Consent
                      {consentData?.status === 'opted-in' && (
                        <span className="ml-2 tag is-success is-small">Active</span>
                      )}
                      {consentData?.status === 'pending' && (
                        <span className="ml-2 tag is-warning is-small">Pending</span>
                      )}
                      {consentData?.status === 'opted-out' && (
                        <span className="ml-2 tag is-danger is-small">Opted Out</span>
                      )}
                    </a>
                  </li>
                )}
              </ul>
            </div>

            {/* Contact Info Tab */}
            {activeTab === 'contact' && (
              <form onSubmit={handleSubmit} className="box">
                <div className="columns">
                  <div className="column is-half">

                    {/* First Name */}
                    <div className="field">
                      <label className="label">First Name *</label>
                      <div className="control">
                        <input 
                          className={`input ${errors.firstName ? 'is-danger' : ''}`}
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          placeholder="Enter first name"
                        />
                      </div>
                      {errors.firstName && (
                        <p className="help is-danger">{errors.firstName}</p>
                      )}
                    </div>

                    {/* Last Name */}
                    <div className="field">
                      <label className="label">Last Name *</label>
                      <div className="control">
                        <input 
                          className={`input ${errors.lastName ? 'is-danger' : ''}`}
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          placeholder="Enter last name"
                        />
                      </div>
                      {errors.lastName && (
                        <p className="help is-danger">{errors.lastName}</p>
                      )}
                    </div>

                    {/* Email */}
                    <div className="field">
                      <label className="label">
                        Email Address {emailRequired ? '*' : '(Optional)'}
                      </label>
                      <div className="control">
                        <input 
                          className={`input ${errors.email ? 'is-danger' : ''}`}
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="Enter email address"
                        />
                      </div>
                      {errors.email && (
                        <p className="help is-danger">{errors.email}</p>
                      )}
                      {!emailRequired && (
                        <p className="mx-2 mt-1 is-size-7">Email is optional for Participants</p>
                      )}
                    </div>

                    {/* Phone */}
                    <div className="field">
                      <label className="label">
                        Phone Number {phoneRequired ? '*' : ''}
                      </label>
                      <div className="control has-icons-left">
                        <input 
                          className={`input ${errors.phone ? 'is-danger' : ''}`}
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="1234567890"
                          maxLength="10"
                          style={{ paddingLeft: '45px' }}
                        />
                        <span className="icon is-small is-left" style={{ pointerEvents: 'none', width: '45px' }}>
                          <span style={{ fontSize: '14px', color: '#4a4a4a' }}>+1</span>
                        </span>
                      </div>
                      {errors.phone && (
                        <p className="help is-danger">{errors.phone}</p>
                      )}
                      <p className="mx-2 mt-1 is-size-7">Enter 10-digit US phone number</p>
                    </div>

                    {/* Carrier - Optional */}
                    <div className="field">
                      <label className="label">Carrier</label>
                      <div className="control">
                        <div className="select is-fullwidth">
                          <select 
                            name="carrier"
                            value={formData.carrier}
                            onChange={handleInputChange}
                          >
                            {CARRIER_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <p className="mx-2 mt-1 is-size-7">Mobile carrier (optional)</p>
                    </div>

                  </div>
                  
                  <div className="column is-half">
                    
                    {/* Role */}
                    <div className="field">
                      <label className="label">Role</label>
                      <div className="control">
                        <div className="select is-fullwidth">
                          <select 
                            name="role"
                            value={formData.role}
                            onChange={handleInputChange}
                          >
                            <option value="participant">Participant</option>
                            <option value="host">Host</option>
                            <option value="admin">Admin</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="field">
                      <label className="label">Status</label>
                      <div className="control">
                        <div className="select is-fullwidth">
                          <select 
                            name="status"
                            value={formData.status}
                            onChange={handleInputChange}
                          >
                            <option value="active">Active</option>
                            <option value="archived">Archived</option>
                            <option value="prospecting">Prospecting</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Address Fields */}
                    <div className="field">
                      <label className="label">Street Address</label>
                      <div className="control">
                        <input 
                          className="input"
                          type="text"
                          name="address.street"
                          value={formData.address.street}
                          onChange={handleInputChange}
                          placeholder="Enter street address (optional)"
                        />
                      </div>
                    </div>

                    <div className="columns is-gapless">
                      <div className="column">
                        <div className="field">
                          <label className="my-2 p-0 label">City</label>
                          <div className="control">
                            <input 
                              className="input"
                              type="text"
                              name="address.city"
                              value={formData.address.city}
                              onChange={handleInputChange}
                              placeholder="City"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="column is-narrow">
                        <div className="field ml-2">
                          <label className="my-2 p-0 label">State</label>
                          <div className="control">
                            <input 
                              className="input"
                              type="text"
                              name="address.state"
                              value={formData.address.state}
                              onChange={handleInputChange}
                              placeholder="State"
                              style={{ width: '80px' }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="column is-narrow">
                        <div className="field ml-2">
                          <label className="my-2 p-0 label">Zip</label>
                          <div className="control">
                            <input 
                              className="input"
                              type="text"
                              name="address.zipCode"
                              value={formData.address.zipCode}
                              onChange={handleInputChange}
                              placeholder="Zip"
                              style={{ width: '90px' }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bio */}
                    <div className="field">
                      <label className="label mt-0 pt-0">Bio/Notes</label>
                      <div className="control">
                        <textarea 
                          className="textarea"
                          name="bio"
                          value={formData.bio}
                          onChange={handleInputChange}
                          placeholder="Enter notes or bio (optional)"
                          rows="3"
                        />
                      </div>
                    </div>

                    {/* Email Verified - Only for Admin and Host */}
                    {(formData.role === 'admin' || formData.role === 'host') && (
                      <div className="mx-2 field">
                        <div className="control">
                          <label className="checkbox">
                            <input 
                              type="checkbox"
                              name="emailVerified"
                              checked={formData.emailVerified}
                              onChange={handleInputChange}
                            />
                            <span className="ml-2">Mark email as verified.</span>
                          </label>
                        </div>
                      </div>
                    )}

                  </div>
                </div>

                {/* Submit Button */}
                <div className="field is-grouped mt-4 pt-0 pb-4">
                  <div className="control">
                    <button 
                      className={`button is-primary ${loading ? 'is-loading' : ''}`}
                      type="submit"
                      disabled={loading}
                    >
                      Update Contact
                    </button>
                  </div>
                  <div className="control">
                    <button 
                      className="button is-light"
                      type="button"
                      onClick={() => navigate('/contacts')}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            )}

            {/* SMS Consent Tab */}
            {activeTab === 'consent' && showConsentTab && (
              <div className="box">
                <div className="is-flex is-justify-content-space-between is-align-items-center mb-4">
                  <h2 className="title is-4 mb-0">SMS Consent Management</h2>
                  <button
                    className={`button is-small is-info is-outlined ${consentLoading ? 'is-loading' : ''}`}
                    onClick={handleRefreshConsent}
                    disabled={consentLoading || !formData.phone}
                    title="Refresh consent status"
                  >
                    <span>Refresh</span>
                  </button>
                </div>
                
                {/* Consent Error */}
                {errors.consent && (
                  <div className="notification is-danger is-light">
                    <button 
                      className="delete" 
                      onClick={() => setErrors(prev => ({ ...prev, consent: '' }))}
                    ></button>
                    {errors.consent}
                  </div>
                )}

                {/* How Consent Works Info Box */}
                <div className="notification is-info is-light mb-4">
                  <p className="is-size-7">
                    <strong>How consent works:</strong> Consent status is controlled by the participant via SMS keywords. 
                    When they reply <strong>JOIN</strong> or <strong>START</strong>, they are opted in. 
                    When they reply <strong>STOP</strong>, they are opted out. 
                    This ensures compliance with Twilio and TCPA regulations. 
                    As an admin, you can send opt-in requests but cannot directly change a participant's consent status.
                  </p>
                </div>

                {!formData.phone ? (
                  <div className="notification is-warning is-light">
                    <p>Please add a phone number in the Contact Info tab to manage SMS consent.</p>
                  </div>
                ) : consentLoading ? (
                  <div className="has-text-centered py-4">
                    <p>Loading consent information...</p>
                  </div>
                ) : (
                  <>
                    {/* Opted Out Details Box - Prominent display when opted out */}
                    {consentData?.status === 'opted-out' && (
                      <div className="notification is-danger mb-5" style={{ backgroundColor: '#ffeded', borderLeft: '4px solid #f14668' }}>
                        <div className="columns is-vcentered">
                          <div className="column is-narrow">
                            <span className="icon is-large has-text-danger">
                              <span style={{ fontSize: '48px' }}>🚫</span>
                            </span>
                          </div>
                          <div className="column">
                            <h3 className="title is-5 has-text-danger mb-2">Contact Has Opted Out</h3>
                            <p className="mb-3">
                              This contact cannot receive SMS messages. They were removed from all active programs 
                              and any scheduled messages have been cancelled.
                            </p>
                            
                            {/* Opt-out details */}
                            {consentData.lastOptOutDetails && (
                              <div className="content is-small mb-3" style={{ backgroundColor: 'white', padding: '12px', borderRadius: '4px' }}>
                                <p className="mb-1"><strong>Opt-out Details:</strong></p>
                                <ul className="mt-1 mb-0" style={{ marginLeft: '1.5em' }}>
                                  <li>Date: {formatDate(consentData.lastOptOutDetails.timestamp)}</li>
                                  <li>Method: {consentData.lastOptOutDetails.method?.replace(/-/g, ' ')}</li>
                                  {consentData.lastOptOutDetails.podsRemoved > 0 && (
                                    <li>
                                      Removed from {consentData.lastOptOutDetails.podsRemoved} pod(s)
                                      {consentData.lastOptOutDetails.podNames?.length > 0 && (
                                        <span>: {consentData.lastOptOutDetails.podNames.join(', ')}</span>
                                      )}
                                    </li>
                                  )}
                                  {consentData.lastOptOutDetails.messagesCancelled > 0 && (
                                    <li>{consentData.lastOptOutDetails.messagesCancelled} scheduled message(s) cancelled</li>
                                  )}
                                </ul>
                              </div>
                            )}
                            
                            <p className="is-size-7">
                              <strong>To re-enable messaging:</strong> The contact must text <strong>START</strong> or <strong>JOIN</strong> to your Twilio number.
                              You can also send a new opt-in request below to prompt them to re-subscribe.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Current Consent Status */}
                    <div className="columns">
                      <div className="column is-half">
                        <div className={`box ${consentData?.status === 'opted-out' ? 'has-background-danger-light' : 'backgroundColorGrey'}`}>
                          <h3 className="subtitle is-5 mb-3 is-bold">Current Status</h3>
                          
                          <div className="mb-4">
                            <span className={`tag is-medium ${getStatusBadge(consentData?.status || 'unknown')}`}>
                              {consentData?.status?.toUpperCase() || 'NO CONSENT RECORD'}
                            </span>
                          </div>
                          
                          {consentData ? (
                            <div className="content is-small">
                              <p className='colorBlack'><span className='is-bold'>Phone:</span> +1{formData.phone}</p>
                              <p className='colorBlack'>
                                <span className='is-bold'>Can Receive Messages:</span>{' '}
                                {consentData.canReceiveMessages ? (
                                  <span className="has-text-success has-text-weight-bold">Yes</span>
                                ) : (
                                  <span className="has-text-danger has-text-weight-bold">No</span>
                                )}
                              </p>
                              {consentData.optedInAt && (
                                <p className='colorBlack'><span className='is-bold'>Opted In:</span> {formatDate(consentData.optedInAt)}</p>
                              )}
                              {consentData.optedOutAt && (
                                <p className='colorBlack'><span className='is-bold'>Opted Out:</span> {formatDate(consentData.optedOutAt)}</p>
                              )}
                              {consentData.optInRequestedAt && (
                                <p className='colorBlack'><span className='is-bold'>Last Request Sent:</span> {formatDate(consentData.optInRequestedAt)}</p>
                              )}
                              <p className='colorBlack'><span className='is-bold'>Opt-in Requests Sent:</span> {consentData.optInRequestCount || 0}</p>
                            </div>
                          ) : (
                            <p className="is-size-7">No consent record exists for this phone number yet.</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="column is-half">
                        <div className="box">
                          <h3 className="subtitle is-5 mb-3">Actions</h3>
                          
                          {/* Send Opt-in Request */}
                          {(!consentData || consentData.status !== 'opted-in') && (
                            <div className="mb-4">
                              {!showOptinForm ? (
                                <button
                                  className="button is-primary is-fullwidth"
                                  onClick={() => setShowOptinForm(true)}
                                  disabled={loading}
                                >
                                  {consentData?.status === 'opted-out' 
                                    ? 'Send Re-Opt-In Request' 
                                    : consentData 
                                      ? 'Resend Opt-in Request' 
                                      : 'Send Opt-in Request'}
                                </button>
                              ) : (
                                <div className="box backgroundColorGrey">
                                  <h4 className="subtitle is-6">Opt-in Message</h4>
                                  
                                  <div className="field">
                                    <label className="label is-small">Opt-in Request Message</label>
                                    <div className="control">
                                      <textarea
                                        className="textarea"
                                        value={optinMessage}
                                        onChange={(e) => setOptinMessage(e.target.value)}
                                        rows="5"
                                      />
                                    </div>
                                    <p className="mx-2 mt-1 is-size-7">{optinMessage.length} characters</p>
                                  </div>

                                  {/* Toggle for confirmation messages */}
                                  <div className="field mt-3">
                                    <button
                                      type="button"
                                      className="button is-small is-light"
                                      onClick={() => setShowConfirmationMessages(!showConfirmationMessages)}
                                    >
                                      {showConfirmationMessages ? '▼' : '▶'} Customize Auto-Response Messages
                                    </button>
                                  </div>

                                  {/* Custom Confirmation Messages */}
                                  {showConfirmationMessages && (
                                    <div className="mt-3 p-3" style={{ backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #dbdbdb' }}>
                                      <div className="field">
                                        <label className="label is-small">
                                          Opt-In Confirmation
                                          <span className="has-text-weight-normal has-text-grey ml-2">(sent when user replies JOIN)</span>
                                        </label>
                                        <div className="control">
                                          <textarea
                                            className="textarea is-small"
                                            value={customOptInConfirmation}
                                            onChange={(e) => setCustomOptInConfirmation(e.target.value)}
                                            rows="3"
                                          />
                                        </div>
                                        <p className="mx-2 mt-1 is-size-7">
                                          {customOptInConfirmation === DEFAULT_OPT_IN_CONFIRMATION 
                                            ? `${customOptInConfirmation.length} characters (default)` 
                                            : `${customOptInConfirmation.length} characters (customized)`}
                                        </p>
                                      </div>

                                      <div className="field mt-3">
                                        <label className="label is-small">
                                          Opt-Out Confirmation
                                          <span className="has-text-weight-normal has-text-grey ml-2">(sent when user replies STOP)</span>
                                        </label>
                                        <div className="control">
                                          <textarea
                                            className="textarea is-small"
                                            value={customOptOutConfirmation}
                                            onChange={(e) => setCustomOptOutConfirmation(e.target.value)}
                                            rows="3"
                                          />
                                        </div>
                                        <p className="mx-2 mt-1 is-size-7">
                                          {customOptOutConfirmation === DEFAULT_OPT_OUT_CONFIRMATION 
                                            ? `${customOptOutConfirmation.length} characters (default)` 
                                            : `${customOptOutConfirmation.length} characters (customized)`}
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                  
                                  <div className="field is-grouped mt-4">
                                    <div className="control">
                                      <button
                                        className={`button is-primary ${loading ? 'is-loading' : ''}`}
                                        onClick={handleSendOptIn}
                                        disabled={loading || !optinMessage.trim()}
                                      >
                                        Send Opt-in Message
                                      </button>
                                    </div>
                                    <div className="control">
                                      <button
                                        className="button is-light"
                                        onClick={() => setShowOptinForm(false)}
                                        disabled={loading}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Opted-in confirmation: no further action needed */}
                          {consentData?.status === 'opted-in' && (
                            <div className="notification is-success is-light mb-4">
                              <p className="is-size-7">
                                ✅ This contact is opted in and can receive messages. 
                                If they text <strong>STOP</strong>, they will be automatically opted out and removed from all active pods.
                              </p>
                            </div>
                          )}

                          {/* Edit Confirmation Messages - Show when consent exists */}
                          {consentData && !showOptinForm && (
                            <div className="mb-4">
                              <button
                                type="button"
                                className="button is-small is-light is-fullwidth"
                                onClick={() => setShowConfirmationMessages(!showConfirmationMessages)}
                              >
                                {showConfirmationMessages ? '▼' : '▶'} Edit Auto-Response Messages
                              </button>
                              
                              {showConfirmationMessages && (
                                <div className="mt-3 p-3" style={{ backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                  <div className="field">
                                    <label className="label is-small">
                                      Opt-In Confirmation
                                      <span className="has-text-weight-normal has-text-grey ml-2">(JOIN)</span>
                                    </label>
                                    <div className="control">
                                      <textarea
                                        className="textarea is-small"
                                        value={customOptInConfirmation}
                                        onChange={(e) => setCustomOptInConfirmation(e.target.value)}
                                        rows="3"
                                      />
                                    </div>
                                    <p className="mx-2 mt-1 is-size-7">
                                      {customOptInConfirmation === DEFAULT_OPT_IN_CONFIRMATION 
                                        ? `${customOptInConfirmation.length} characters (default)` 
                                        : `${customOptInConfirmation.length} characters (customized)`}
                                    </p>
                                  </div>

                                  <div className="field mt-3">
                                    <label className="label is-small">
                                      Opt-Out Confirmation
                                      <span className="has-text-weight-normal has-text-grey ml-2">(STOP)</span>
                                    </label>
                                    <div className="control">
                                      <textarea
                                        className="textarea is-small"
                                        value={customOptOutConfirmation}
                                        onChange={(e) => setCustomOptOutConfirmation(e.target.value)}
                                        rows="3"
                                      />
                                    </div>
                                    <p className="mx-2 mt-1 is-size-7">
                                      {customOptOutConfirmation === DEFAULT_OPT_OUT_CONFIRMATION 
                                        ? `${customOptOutConfirmation.length} characters (default)` 
                                        : `${customOptOutConfirmation.length} characters (customized)`}
                                    </p>
                                  </div>

                                  <div className="field mt-3">
                                    <button
                                      className={`button is-small is-info ${loading ? 'is-loading' : ''}`}
                                      onClick={handleSaveConfirmationMessages}
                                      disabled={loading}
                                    >
                                      Save Confirmation Messages
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Consent History */}
                    {consentHistory.length > 0 && (
                      <div className="mt-5">
                        <h3 className="subtitle is-5 mb-3 is-bold">Consent History</h3>
                        <div className="table-container">
                          <table className="table is-fullwidth is-striped is-narrow">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Action</th>
                                <th>Method</th>
                                <th>Details</th>
                              </tr>
                            </thead>
                            <tbody>
                              {consentHistory.map((entry, index) => (
                                <tr key={index}>
                                  <td className="is-size-7">{formatDate(entry.timestamp)}</td>
                                  <td>
                                    <span className={`tag is-small ${
                                      entry.action.includes('opted-in') || entry.action === 'resubscribed' ? 'is-success' :
                                      entry.action.includes('opted-out') ? 'is-danger' :
                                      entry.action.includes('requested') ? 'is-warning' : 'is-light'
                                    }`}>
                                      {entry.action.replace(/-/g, ' ')}
                                    </span>
                                  </td>
                                  <td className="is-size-7">{entry.method?.replace(/-/g, ' ')}</td>
                                  <td className="is-size-7">
                                    {entry.keyword && <span>Keyword: {entry.keyword}</span>}
                                    {entry.notes && <span>{entry.notes}</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

          </div>
        </div>
      </section>
    </>
  );
};

export default EditContact;